<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject, onMounted, provide, reactive, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import SidebarMenu, { SidebarMenuGroup } from '@shared/components/SidebarMenu.vue';
    import { EVENT_EDITOR_ICONS } from './event-editor-icons';
    import Loading from '@shared/components/Loading.vue';
    import SavingIndicator from '@shared/components/SavingIndicator.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import AclEditor from '@cwd/Acl/AclEditor.vue';
    import EventCopySettingsModal from '@views/Event/EventCopySettingsModal.vue';
    import EventUpdateEnabledAlert from '@views/Event/EventEditor/EventUpdateEnabledAlert.vue';
    import EventMetaInfo from '@views/Event/EventEditor/EventMetaInfo.vue';
    import EventEditorPublish from '@views/Event/EventEditor/EventEditorPublish.vue';
    import PopoverSurvey from '@shared/components/PopoverSurvey.vue';
    import { AccessControlLevel, type CurrentObject } from '@cwd/Acl/Acl.access';
    import { hasAccessLevel } from '@cwd/Acl/useAclAccess';
    import { setupCopyEvent } from '@views/Event/New';
    import { loadEvent, updateEventTags } from '@views/Event/Event.access';
    import { useEventPropertySaves } from '@views/Event/EventEditor/use-event-property-saves';
    import { type Event, type Tag, EventStatusNames } from '@views/Event/EventTypes';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import ImageLibraryModal from '@shared/components/Images/ImageLibraryModal.vue';
    import { setupImageLibrary } from '@shared/components/Images/ImageLibrary';
    import { ImageLibraryProvide, imageLibraryProvideName } from '@shared/components/Images/types';
    import LinkEditor from '@shared/components/RichEditor/LinkEditor.vue';
    import ColorPickerSwatches from '@shared/components/ColorPickers/VACP/ColorPickerSwatches.vue';
    import AiEditor from '@cwd/AiTools/AiEditor.vue';
    import { setupRichEditorProvides } from '@shared/components/RichEditor/setupProvides';
    import BannerEvaluation from '@cwd/Account/BannerEvaluation/BannerEvaluation.vue';
    import PaymentFailed from '@views/Dashboard/components/PaymentFailed.vue';
    import {
        canEditKey,
        canWriteKey,
        eventKey,
        queueEventPropertyKey,
        saveEventPropertyIfChangedKey,
        saveEventPropertyKey,
        saveEventTagsKey,
        trackSaveKey,
    } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const state = reactive<{
        event: Event | null;
        isLoading: boolean;
        initialized: boolean;
        loadFailed: boolean;
        pendingSaves: number;
    }>({
        event: null,
        isLoading: false,
        initialized: false,
        loadFailed: false,
        pendingSaves: 0,
    });

    const route = useRoute();
    const router = useRouter();

    const isSaving = computed(() => state.pendingSaves > 0);

    // Parity with legacy EditEventViewModel: updateEnabled defaults to false and is only
    // opened up once the loaded event turns out not to be Active.
    const updateEnabled = ref(false);
    const showPopoverSurvey = ref(false);

    provide(
        eventKey,
        computed(() => state.event)
    );

    // Parity with the legacy EditEventViewModel.userHasWriteAccess: only the server-computed,
    // group-aware CurrentUserAccessLevel gates editing here, no separate feature-scope check.
    const canWrite = computed(() => hasAccessLevel(state.event?.CurrentUserAccessLevel, AccessControlLevel.Write));
    const canEdit = computed(() => canWrite.value && updateEnabled.value);

    provide(canWriteKey, canWrite);
    provide(canEditKey, canEdit);

    const { saveEventProperty, saveEventPropertyIfChanged, queueEventProperty } = useEventPropertySaves(state, canWrite);

    provide(saveEventPropertyKey, saveEventProperty);
    provide(saveEventPropertyIfChangedKey, saveEventPropertyIfChanged);
    provide(queueEventPropertyKey, queueEventProperty);

    async function saveEventTags(tags: Tag[]) {
        if (!canWrite.value) return;
        state.pendingSaves++;
        try {
            await updateEventTags(state.event!.EventId, tags, state.event?.Status === EventStatusNames.Active);
            state.event!.Tags = tags;
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: $Resources.TagsLabel });
        } finally {
            state.pendingSaves--;
        }
    }

    provide(saveEventTagsKey, saveEventTags);

    // Lets children with their own save calls drive the shared saving indicator.
    async function trackSave<T>(work: () => Promise<T>): Promise<T> {
        state.pendingSaves++;
        try {
            return await work();
        } finally {
            state.pendingSaves--;
        }
    }

    provide(trackSaveKey, trackSave);

    const imageLibrary = setupImageLibrary();
    provide<ImageLibraryProvide>(imageLibraryProvideName, {
        state: imageLibrary.state,
        newSrc: imageLibrary.newSrc,
        insert: imageLibrary.insert,
        load: imageLibrary.load,
        toggleShowLibrary: imageLibrary.toggleShowLibrary,
        uploadFile: imageLibrary.uploadFile,
        uploadFromUrl: imageLibrary.uploadFromUrl,
        uploadFromBase64: imageLibrary.uploadFromBase64,
        navigateToAiEdit: imageLibrary.navigateToAiEdit,
    });

    const { aiEditor } = setupRichEditorProvides();

    onMounted(async () => {
        state.isLoading = true;
        try {
            state.event = await loadEvent(props.eventId);
            if (state.event?.Status !== EventStatusNames.Active) updateEnabled.value = true;
            state.initialized = true;

            // No child route in the URL: drafts start at the first details step, other events at the dashboard.
            if (route.name === 'EventEditorRoot') {
                const name = state.event?.Status === EventStatusNames.Draft ? 'EventEditorDetailsNameAndDescription' : 'EventEditorDashboard';
                await router.replace({ name, params: { eventId: props.eventId } });
            }
        } catch {
            state.loadFailed = true;
        } finally {
            state.isLoading = false;
        }
    });

    const onPublished = () => {
        showPopoverSurvey.value = true;
        router.push({ name: 'EventEditorDashboard', params: { eventId: props.eventId } });
    };

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const statusLabel = computed(() => {
        if (!state.event) return '';
        return ($Resources as Record<string, string>)['EventStatus' + state.event?.Status] ?? String(state.event?.Status);
    });

    const showAclModal = ref(false);
    const aclSelectedGroups = computed<CurrentObject[]>(() =>
        (state.event?.AccessControlList ?? []).map(
            (acl) =>
                ({
                    ...acl,
                    GroupId: acl.PrincipalId,
                    AccessControlList: acl,
                }) as CurrentObject
        )
    );

    const onSaveAcl = (newValue: CurrentObject[]) => {
        // AclEditor emits the selected groups with the ACL fields flattened onto them.
        if (state.event)
            state.event.AccessControlList = newValue.map((group) => ({
                ObjectId: group.ObjectId,
                PrincipalId: group.PrincipalId,
                AccessControlLevel: group.AccessControlLevel,
                PrincipalName: group.PrincipalName,
                Type: group.Type,
            }));
        showAclModal.value = false;
    };

    const { copyEventState, onCopyEvent, handleCloseCopySettings } = setupCopyEvent();
    const copyRestrictedByRelativeSchedule = computed(
        () => copyEventState.sourceEventUseRelativeSchedule && !featureAccess.hasFeature('Events.RelativeSchedule')
    );

    const openCopyEventModal = () => {
        if (!state.event) return;
        copyEventState.eventId = props.eventId;
        copyEventState.sourceEventUseRelativeSchedule = state.event?.UseRelativeSchedule;
        copyEventState.showCopySettings = true;
    };

    const menuGroups = computed<SidebarMenuGroup[]>(() => [
        { routeNames: ['EventEditorDashboard'], label: $Resources.DashboardMenuItem, icon: EVENT_EDITOR_ICONS.dashboard },
        {
            heading: $Resources.EventNavDetails,
            icon: EVENT_EDITOR_ICONS.info,
            submenu: true,
            children: [
                { routeNames: ['EventEditorDetailsNameAndDescription'], label: $Resources.EventNavDetailsGeneral },
                { routeNames: ['EventEditorDetailsHero'], label: $Resources.EventNavDetailsHero },
                { routeNames: ['EventEditorDetailsContactPerson'], label: $Resources.EventNavDetailsContactPerson },
                { routeNames: ['EventEditorDetailsStartAndEnd'], label: $Resources.EventNavDetailsStartAndEnd },
                { routeNames: ['EventEditorDetailsRegistration'], label: $Resources.EventNavDetailsRegistration },
                { routeNames: ['EventEditorDetailsParticipantLimit'], label: $Resources.EventNavDetailsParticipantLimit },
                { routeNames: ['EventEditorDetailsLocation'], label: $Resources.EventNavDetailsLocation },
                { routeNames: ['EventEditorDetailsOrganizer'], label: $Resources.EventNavDetailsOrganizer },
            ],
        },
        { routeNames: ['EventEditorSettings'], label: $Resources.SettingsTab, icon: EVENT_EDITOR_ICONS.settings },
        { routeNames: ['EventEditorEntryForm'], label: $Resources.EventNavEntryForm, icon: EVENT_EDITOR_ICONS.form },
        {
            heading: $Resources.EventNavCommunication,
            icon: EVENT_EDITOR_ICONS.mail,
            submenu: true,
            children: [
                { routeNames: ['EventEditorConfirmations'], label: $Resources.EventNavConfirmations },
                ...(state.event?.RequireAttestation ? [{ routeNames: ['EventEditorAttestation'], label: $Resources.EventNavAttestation }] : []),
                { routeNames: ['EventEditorIssues'], label: $Resources.EventIssuesHeading },
                { routeNames: ['EventEditorCommunicationSettings'], label: $Resources.EventCommunicationSettings },
            ],
        },
        { routeNames: ['EventEditorLandingPage'], label: $Resources.EventNavLandingPage, icon: EVENT_EDITOR_ICONS.web },
        {
            heading: $Resources.EventParticipantsLabel,
            icon: EVENT_EDITOR_ICONS.group,
            submenu: true,
            children: [
                { routeNames: ['EventEditorSpeakers'], label: $Resources.EventNavSpeakers },
                { routeNames: ['EventEditorSponsors'], label: $Resources.SponsorHeading },
            ],
        },
        { routeNames: ['EventEditorGuestList'], label: $Resources.EventNavGuestlist, icon: EVENT_EDITOR_ICONS.list },
        { routeNames: ['EventEditorLive'], label: $Resources.EventNavLive, icon: EVENT_EDITOR_ICONS.live },
    ]);
</script>

<template>
    <div class="has-sidebar">
        <nav role="navigation" class="sidebar set-height custom-scrollbar navbar-sticky d-flex flex-direction-col">
            <EventEditorPublish
                v-if="state.event"
                :event="state.event"
                :canWrite="canWrite"
                v-model:updateEnabled="updateEnabled"
                @update:status="(status) => (state.event!.Status = status)"
                @published="onPublished" />

            <SidebarMenu :items="menuGroups" :params="{ eventId }" />

            <div class="form-group d-flex flex-direction-col gap-xs m-auto-t">
                <FeatureLock feature="Events.Publish" hideWhenLocked>
                    <button type="button" class="btn btn-default btn-block btn-sm" @click="openCopyEventModal">
                        {{ $Resources.CopyThisEventBtn }}
                    </button>
                </FeatureLock>

                <template v-if="featureAccess.hasFeature('Users.Acl') || (state.event && state.event.AccessControlList.length)">
                    <button
                        type="button"
                        class="btn btn-default btn-block btn-sm btn-flex"
                        :class="{ 'btn-primary': (state.event?.AccessControlList.length ?? 0) > 0 }"
                        @click="showAclModal = true">
                        <template v-if="state.event?.AccessControlList.length === 0">
                            <EventIcon icon="lockOpen" :size="16" />
                            <span>{{ $Resources.EditAclBtnOff }}</span>
                        </template>
                        <template v-else>
                            <EventIcon icon="lock" :size="16" />
                            <span>{{ $Resources.EditAclBtnOn }}</span>
                        </template>
                    </button>
                </template>
            </div>
        </nav>

        <section class="view-main d-flex flex-direction-col m-lg-t">
            <EventUpdateEnabledAlert v-if="canWrite && state.event?.Status === EventStatusNames.Active" v-model="updateEnabled" class="max-1200" />

            <div class="max-1200">
                <BannerEvaluation />

                <PaymentFailed />

                <BsAlert
                    v-if="!canWrite"
                    :type="AlertTypes.Warning"
                    :dismissible="false"
                    :title="$Resources.EventReadOnlyAccessHeading"
                    :content="$Resources.EventReadOnlyAccessBody" />

                <FeatureLock feature="Events.Publish">
                    <template #locked>
                        <BsAlert
                            :type="AlertTypes.Warning"
                            :dismissible="false"
                            :title="$Resources.EventCantPublishHeader"
                            :content="$Resources.EventCantPublishBody" />
                    </template>
                </FeatureLock>

                <header class="event-header m-lg-b">
                    <h1 class="h3 m-0-t">
                        {{ state.event?.Name || $Resources.NoTitlePlaceholder }}

                        <span class="status-label round text-md m-xs-l">
                            <EventIcon icon="circle" :size="16" class="status-circle m-xs-r" :class="'status-' + String(state.event?.Status).toLowerCase()" />
                            <span>
                                {{ $Resources.EventCurrentStatusLabel }}:
                                <strong>{{ statusLabel.toLowerCase() }}</strong>
                            </span>
                        </span>
                    </h1>

                    <EventMetaInfo :event="state?.event" />
                </header>
            </div>

            <BsAlert
                v-if="state.loadFailed"
                class="max-1200"
                :type="AlertTypes.Danger"
                :dismissible="false"
                :title="$Resources.ErrorHeading"
                :content="$Resources.ErrorTechnicalIssuesBody" />
            <router-view v-else :eventId="eventId"></router-view>
        </section>
    </div>

    <Loading :show="state.isLoading" :loadingStyle="state.initialized ? 'progress-bar' : 'spinner'" fixed :overlay="!state.initialized"></Loading>

    <SavingIndicator :show="isSaving" />

    <ImageLibraryModal />

    <LinkEditor></LinkEditor>
    <ColorPickerSwatches></ColorPickerSwatches>
    <AiEditor
        v-if="aiEditor"
        :getContent="aiEditor.getState.value.getContent"
        :show="aiEditor.getState.value.show"
        @update:content="(newValue: string) => aiEditor!.getState.value.updateContent(newValue)"
        @close="aiEditor.closeEditor"></AiEditor>

    <AclEditor :selected-groups="aclSelectedGroups" type="Events" :objectId="eventId" :open="showAclModal" @save="onSaveAcl" @close="showAclModal = false" />

    <EventCopySettingsModal
        :show="copyEventState.showCopySettings"
        :isLoading="copyEventState.isLoading"
        :formData="copyEventState.form.data"
        :copyRestrictedByRelativeSchedule="copyRestrictedByRelativeSchedule"
        :copySignUpFormMissing="copyEventState.copySignUpFormMissing"
        @close="handleCloseCopySettings"
        @update:modelValue="(key, newVal) => (copyEventState.form.data[key].modelValue = newVal)"
        @submit="onCopyEvent" />

    <PopoverSurvey v-if="showPopoverSurvey" id="5a442d2d-5d37-4d52-a162-b8a9d7a77c9d" />
</template>

<style>
    .status-label {
        line-height: 1;
        padding: var(--ug-spacing-xs) var(--ug-spacing-md) var(--ug-spacing-xs) var(--ug-spacing-xs);
        background: var(--brand-secondary-100);
        color: var(--text-color);
        display: inline-flex;
        align-items: center;
    }

    .event-header {
        padding-bottom: var(--ug-spacing-lg);
        border-bottom: 1px solid var(--border-color);
    }
</style>
